import { AlertTriangle, Copy, Home, RefreshCw } from 'lucide-react';
import type React from 'react';
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isDarkMode: boolean;
  copied: boolean; // 복사 성공 표시 상태 추가
}

class CustomErrorBoundary extends Component<Props, State> {
  private observer: MutationObserver | null = null;
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isDarkMode: this.checkDarkMode(),
      copied: false,
    };
  }
  checkDarkMode = (): boolean => {
    return document.documentElement.classList.contains('dark');
  };
  componentDidMount() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.setState({ isDarkMode: this.checkDarkMode() });
        }
      });
    });
    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });
    console.error('CustomErrorBoundary caught an error:', error, errorInfo);
    if (process.env.NODE_ENV === 'production') {
      // Sentry.captureException(error, { extra: errorInfo })
    }
  }
  handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      copied: false,
    });
  };
  handleGoHome = () => {
    window.location.href = '/';
  };
  handleCopy = () => {
    const { error, errorInfo } = this.state;
    if (!error) return;

    const textToCopy = [`Error: ${error.message}`, error.stack || '', errorInfo?.componentStack || '']
      .filter(Boolean)
      .join('\n\n');

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          this.setState({ copied: true });
          setTimeout(() => this.setState({ copied: false }), 2000);
        })
        .catch(() => {
          alert('복사에 실패했습니다. 수동으로 복사해주세요.');
        });
    } else {
      // fallback: 구버전 브라우저 등에서 execCommand 사용
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed'; // 화면안 보이게
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          this.setState({ copied: true });
          setTimeout(() => this.setState({ copied: false }), 2000);
        } else {
          alert('복사에 실패했습니다. 수동으로 복사해주세요.');
        }
      } catch {
        alert('복사 기능이 지원되지 않는 환경입니다.');
      }
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const { isDarkMode, error, errorInfo, copied } = this.state;
      return (
        <div
          className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`}>
          <div className='sm:mx-auto sm:w-full sm:max-w-md'>
            <div
              className={`py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
              <div className='flex flex-col items-center relative'>
                <AlertTriangle className={`h-16 w-16 mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  문제가 발생했습니다
                </h2>
                <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  예상치 못한 오류가 발생했습니다. <br />
                  잠시 후 다시 시도해 주세요.
                </p>
                {this.props.showDetails && error && (
                  <details className='w-full mb-6'>
                    <summary
                      className={`cursor-pointer text-sm transition-colors hover:${
                        isDarkMode ? 'text-gray-200' : 'text-gray-700'
                      } text-gray-500 dark:text-gray-400 flex items-center justify-between`}>
                      오류 상세 정보
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          this.handleCopy();
                        }}
                        aria-label='Copy error details'
                        className={`ml-2 p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition`}
                        title='오류 복사'>
                        <Copy className='w-4 h-4' />
                      </button>
                    </summary>
                    <div
                      className={`mt-2 p-3 rounded text-xs font-mono overflow-auto max-h-32 transition-colors whitespace-pre-wrap ${
                        isDarkMode
                          ? 'bg-gray-700 border border-gray-600 text-gray-200'
                          : 'bg-gray-100 border border-gray-200 text-gray-800'
                      }`}>
                      <div className='font-semibold'>Error: {error.message}</div>
                      <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                        Stack: {error.stack}
                      </div>
                      {errorInfo && <pre className='mt-2'>{errorInfo.componentStack}</pre>}
                    </div>
                    {copied && (
                      <div
                        className={`mt-2 text-center text-sm font-semibold ${
                          isDarkMode ? 'text-green-400' : 'text-green-600'
                        }`}>
                        복사되었습니다 ✔
                      </div>
                    )}
                  </details>
                )}
                <div className='flex space-x-3 w-full'>
                  <button
                    onClick={this.handleReset}
                    className={`flex-1 flex justify-center items-center px-4 py-2 text-white rounded-md shadow-sm text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700 focus:ring-offset-gray-800'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-offset-white'
                    }`}>
                    <RefreshCw className='h-4 w-4 mr-2' />
                    다시 시도
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className={`flex-1 flex justify-center items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'text-gray-200 bg-gray-700 border border-gray-600 hover:bg-gray-600 focus:ring-offset-gray-800'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-offset-white'
                    }`}>
                    <Home className='h-4 w-4 mr-2' />
                    홈으로
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CustomErrorBoundary;
